import CheckIcon from "@mui/icons-material/Check";

type Props = {
  budgetOptions: number[];
  selectedBudget: number | null;
  onSelect: (amount: number) => void;
  centered?: boolean;
  /**
   * Read-only grid: the campaign is past draft, so the tier can no longer change. Nothing is
   * hidden — the selected chip still reads as selected, it just cannot be moved. Defaults to
   * `false`, which is why the Add-More tier picker (AddMoreView) is unaffected: it never passes
   * this prop, so the Pro reorder flow keeps its own fully interactive chips.
   */
  locked?: boolean;
};

export default function BudgetChips({
  budgetOptions,
  selectedBudget,
  onSelect,
  centered = false,
  locked = false,
}: Props) {
  return (
    <div
      className="px-5 py-5"
      // Same treatment as the locked Accordion body (Accordion.tsx) so every locked surface in
      // the builder reads the same.
      style={locked ? { opacity: 0.55, pointerEvents: "none", userSelect: "none" } : undefined}
    >
      <div className={`flex flex-wrap gap-2.5 ${centered ? "justify-center" : ""}`}>
        {budgetOptions.map((amount) => {
          const selected = selectedBudget === amount;
          return (
            <button
              key={amount}
              type="button"
              // pointer-events on the wrapper kills the mouse; tabIndex + aria-disabled keep the
              // chip out of the keyboard path and announce it as inert to screen readers.
              onClick={() => !locked && onSelect(amount)}
              aria-disabled={locked || undefined}
              tabIndex={locked ? -1 : undefined}
              className={`inline-flex items-center justify-center gap-1.5 min-w-[90px] rounded-pill font-lato text-[14px] font-semibold text-rodeo-black transition-all duration-150 ${
                selected
                  ? "border-2 border-[var(--color-dark-dust)] bg-clear-dust py-[7px] px-[13px]"
                  : "border border-[var(--color-smoke)] hover:border-[var(--color-rodeo-dust)] bg-white py-2 px-3.5"
              }`}
            >
              {selected && (
                <span
                  className="w-5 h-5 rounded-full inline-flex items-center justify-center"
                  style={{ backgroundColor: "var(--color-rodeo-black)" }}
                >
                  <CheckIcon sx={{ fontSize: 14, color: "white" }} />
                </span>
              )}
              ${amount}
            </button>
          );
        })}
      </div>
    </div>
  );
}
